/**
 * liquiditaetMappers (#1315) — narrow the generic
 * ``DerivedFact.fact_value: Record<string, unknown>`` returned by
 * twinService into the typed domain shapes. Seed rows are controlled,
 * but these stay defensive (coerce numbers, default optionals) so one
 * malformed row can't crash the whole page.
 */
import { isPlanWriteOfRecordSource } from './types';
import type { DerivedFact } from '../../../services/twinService';
import {
  CATEGORY_META,
  categoryLabel,
  crosswalkDomainToCategory,
  isLedgerCategory,
  type LedgerCategory,
} from './ledgerCategory';
import type {
  BankTransaction,
  Direction,
  LiquiditaetConfig,
  LiquiditaetRecord,
  PlannedTransaction,
  RecordRole,
  StatementBalance,
} from './types';

const round2 = (n: number): number => Math.round(n * 100) / 100;

const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);

/** Coerce to a finite number; ``fallback`` (default 0) when unusable. */
const num = (v: unknown, fallback = 0): number => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : fallback;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  }
  return fallback;
};

/** Like ``num`` but preserves an explicit ``null`` (e.g. pending KK-Limit). */
const numOrNull = (v: unknown): number | null => (v == null ? null : num(v));

const direction = (v: unknown): Direction => (v === 'income' ? 'income' : 'expense');

/**
 * First crosswalk candidate whose sign is consistent with ``want`` (a ``both``-
 * direction category fits either sign); ``unsorted`` when the whole shortlist
 * clashes (or is empty).
 *
 * #1622: the crosswalk shortlist is direction-blind and §4.6 authored it expense-
 * heavy, so a mixed-sign domain heads with the wrong sign for some rows — e.g.
 * ``finance → [fin_interest(out), …, sub_basic(in), …]``. Judging only the head
 * would push every legacy ``finance`` income row (a subsidy payout) to
 * ``unsorted``; scanning the shortlist lets such a row land on the first incoming
 * candidate (``sub_basic``) while an expense row still keeps the head
 * (``fin_interest``). A domain whose entire shortlist clashes still falls back to
 * ``unsorted`` rather than assert the wrong sign.
 */
export const firstSignCompatibleCategory = (
  candidates: readonly LedgerCategory[],
  want: 'in' | 'out',
): LedgerCategory => {
  const match = candidates.find((c) => {
    const catDir = CATEGORY_META[c].direction;
    return catDir === want || catDir === 'both';
  });
  return match ?? 'unsorted';
};

/**
 * Resolve a fact's ledger_category (#1612). New rows store ``ledger_category``
 * directly; a legacy pre-#1612 row stored only ``domain`` (a 9-domain id) — those
 * still render by crosswalking the domain to a sane category (migration fallback,
 * so no un-migrated row crashes the page before subtask 02 re-seeds). An unknown
 * value (free-text, missing) falls back to the explicit ``unsorted`` bucket.
 *
 * The crosswalk shortlist is direction-blind, so the legacy ``domain`` resolves by
 * scanning its candidate shortlist for the first sign-compatible category (see
 * ``firstSignCompatibleCategory``) — never just the head, which for a mixed-sign
 * domain (e.g. ``finance``) would mis-bucket income rows to ``unsorted``.
 */
const ledgerCategory = (fv: Record<string, unknown>): LedgerCategory => {
  if (isLedgerCategory(fv.ledger_category)) return fv.ledger_category;
  // Fall through to the legacy domain when no valid category is set (missing OR
  // an invalid/free-text value). Matches the "(or an invalid) category" intent.
  if (fv.domain != null) {
    const want = direction(fv.direction) === 'income' ? 'in' : 'out';
    return firstSignCompatibleCategory(crosswalkDomainToCategory.shortlist(fv.domain), want);
  }
  return 'unsorted';
};

export function toConfig(fact: DerivedFact): LiquiditaetConfig {
  const fv = fact.fact_value;
  return {
    anchor_date: str(fv.anchor_date),
    anchor_balance_eur: num(fv.anchor_balance_eur),
    kk_limit_eur: numOrNull(fv.kk_limit_eur),
    wj_start_month: num(fv.wj_start_month, 7),
  };
}

export function toPlanned(fact: DerivedFact): PlannedTransaction {
  const fv = fact.fact_value;
  const qty = fv.qty_meta;
  const qtyMeta =
    qty && typeof qty === 'object'
      ? {
          ha: numOrNull((qty as Record<string, unknown>).ha) ?? undefined,
          t: numOrNull((qty as Record<string, unknown>).t) ?? undefined,
          eur_per_t: numOrNull((qty as Record<string, unknown>).eur_per_t) ?? undefined,
        }
      : undefined;
  const category = ledgerCategory(fv);
  const rawFactor = numOrNull(fv.factor);
  const planFactor = rawFactor != null && rawFactor > 0 ? rawFactor : null;
  const rawLag = numOrNull(fv.lag_days);
  const planLagDays = rawLag != null && rawLag >= 0 ? Math.trunc(rawLag) : null;
  return {
    kind: 'planned',
    ledger_category: category,
    // Human label, with the German category name as a graceful fallback for
    // label-less seed rows (the raw enum id is never shown).
    label: str(fv.label) || categoryLabel(category),
    direction: direction(fv.direction),
    amount_eur: num(fv.amount_eur),
    qty: numOrNull(fv.qty) ?? undefined,
    unit_price_eur: numOrNull(fv.unit_price_eur) ?? undefined,
    value_date: str(fv.value_date),
    period_ym: str(fv.period_ym) || str(fv.value_date).slice(0, 7),
    qty_meta: qtyMeta,
    // HOF-135 §3 — the widened vocabulary reads through as itself. Everything the
    // backend does not recognise still collapses to the legacy seed value, so an
    // unknown source can never masquerade as ``user`` and pick up the D2 overlay
    // routing that selects on exactly that string.
    source: isPlanWriteOfRecordSource(fv.source) ? fv.source : 'ws26_27_excel',
    derived_fact_id: fact.derived_fact_id,
    notes: typeof fv.notes === 'string' && fv.notes ? fv.notes : undefined,
    // HOF-135 §3 — plan provenance. Conditional keys throughout: a pre-HOF-135 row
    // has none of them and must map to exactly the object it mapped to before.
    ...(typeof fv.method === 'string' && fv.method ? { method: fv.method } : {}),
    // ONE coercion each, then guard the RESULT (codex round 2, findings 4/9): the
    // guard-then-recoerce pair let a garbage string ('n/a') pass ``numOrNull``'s
    // null-check and emit ``num``'s 0 fallback — a factor the backend schema
    // explicitly forbids (gt=0: „a zero multiplier is a deleted row wearing a
    // plan's clothes"). Malformed provenance is ABSENT provenance, never a zero.
    ...(planFactor != null ? { factor: planFactor } : {}),
    ...(planLagDays != null ? { lag_days: planLagDays } : {}),
    ...(fv.is_plug === true ? { is_plug: true } : {}),
    ...(typeof fv.assumption === 'string' && fv.assumption
      ? { assumption: fv.assumption }
      : {}),
    ...(typeof fv.base_ref === 'string' && fv.base_ref ? { base_ref: fv.base_ref } : {}),
    // #2211: future-proof subtask 12's read-back — carry account_id when the write
    // path stamped one (conditional key; account-less plans stay account-less).
    ...(typeof fv.account_id === 'string' && fv.account_id ? { account_id: fv.account_id } : {}),
    // #2678 c: the farmer-picked Gegenpartei. Same conditional key — the modal seeds
    // its picker from this read-back, and an edit re-sends it, so dropping it here
    // would silently unlink the row on the next save (wholesale fact_value overwrite).
    ...(typeof fv.counterparty_id === 'string' && fv.counterparty_id
      ? { counterparty_id: fv.counterparty_id }
      : {}),
  };
}

export function toBank(fact: DerivedFact): BankTransaction {
  const fv = fact.fact_value;
  const b: BankTransaction = {
    kind: 'fact',
    booking_date: str(fv.booking_date),
    amount_eur: num(fv.amount_eur),
    counterparty: str(fv.counterparty),
    purpose: str(fv.purpose),
    statement_id: str(fv.statement_id),
    period_ym: str(fv.period_ym) || str(fv.booking_date).slice(0, 7),
  };
  // #2161: pre-categorised flows carry the category on the fact — pass it
  // through untouched (validated in bankToRecord); key stays absent otherwise.
  if (typeof fv.ledger_category === 'string' && fv.ledger_category) {
    b.ledger_category = fv.ledger_category;
  }
  // #2211: the owning bank account, present on every WF bank fact — carry it so the
  // feed can scope by Konto (same conditional-key style; absent → account-less).
  if (typeof fv.account_id === 'string' && fv.account_id) {
    b.account_id = fv.account_id;
  }
  // #3137: the settlement join key. Same conditional-key style — absent on every
  // unsettled line and on every fact minted before the re-materialise, so the
  // absent case is the norm and must never become ''.
  if (typeof fv.planned_ref === 'string' && fv.planned_ref) {
    b.planned_ref = fv.planned_ref;
  }
  // #3160 (FE mirror of #3142's Python to_bank): the resolved counterparty link
  // and HOW it was established. This function builds a WHITELISTED object, so a
  // key the materialiser writes but this mapper does not name is dropped before
  // any read surface sees it — the two-mirrors trap, worker/ARCH.md
  // § counterparty_id provenance (#3142). Conditional and type-gated exactly as
  // above: legacy facts carry neither key and must keep their shape
  // byte-for-byte, and a JSON true would stringify to the perfectly id-shaped
  // 'True'. The source rides INSIDE the id's branch — a provenance for a link
  // that is not there describes nothing and would let the ledger render
  // „automatisch zugeordnet" beside an empty Gegenpartei.
  if (typeof fv.counterparty_id === 'string' && fv.counterparty_id) {
    b.counterparty_id = fv.counterparty_id;
    if (typeof fv.counterparty_id_source === 'string' && fv.counterparty_id_source) {
      b.counterparty_id_source = fv.counterparty_id_source;
    }
  }
  // #3187 (mirror 3 of THREE — worker materialiser → backend liquiditaet_reads.to_bank
  // → here): the Notiz. Same whitelist trap as the block above, and the same reason it
  // is not optional: a bank actual reaches the ledger ONLY through this fact — the
  // entity route is filtered to status === 'contracted' (contractedRecordsFrom), so it
  // can never rescue a booking. That is how #3027's 673 WF Notizen sat on their
  // cash_flow entities, invisible, for two sprints.
  //
  // Conditional and type-gated exactly as above: absent on every fact minted before
  // the #3187 re-materialise, and a JSON true would stringify to a plausible 'True'.
  if (typeof fv.notes === 'string' && fv.notes) {
    b.notes = fv.notes;
  }
  return b;
}

export function toStatement(fact: DerivedFact): StatementBalance {
  const fv = fact.fact_value;
  const s: StatementBalance = {
    statement_id: str(fv.statement_id),
    start_date: str(fv.start_date),
    end_date: str(fv.end_date),
    // ⚠️ ``numOrNull``, NOT ``num`` — HOF-97 codex round 2, P1. ``num``'s fallback is 0,
    // so a statement that legitimately carries only an Alter Saldo arrived as
    // ``endsaldo_eur: 0`` — a perfectly well-formed number, which ``statementAnchors``
    // then accepted and used to RESET the account to zero euros. The engine's "an absent
    // Saldo stays absent, never read as 0,00" rule was real but unreachable: the tests
    // that exercised it built statements by hand and never crossed this mapper.
    anfangssaldo_eur: numOrNull(fv.anfangssaldo_eur),
    endsaldo_eur: numOrNull(fv.endsaldo_eur),
  };
  // #2690: this family carries NO account key inside fact_value (the #2004 lock),
  // so its account arrives on the fact ANCHOR — #2612's materialiser emits one fact
  // per (account, statement) anchored on the financial_account itself. Only lift a
  // genuine financial_account anchor: seed-era rows anchor on
  // ``entity_type='liquiditaet'``, and stamping THAT id here would give them an
  // account no tenant has, so the per-account router would drop every legacy
  // statement instead of falling back to the alias.
  if (fact.entity_type === 'financial_account' && fact.entity_id) {
    s.account_id = fact.entity_id;
  }
  // HOF-97 (A) — carry the write-side verdict through so ``statementAnchors`` can
  // refuse to re-anchor on a contested Endzeile. A strict ``=== true`` rather than a
  // truthiness read: absent means UNRECORDED (every fact minted before this shipped),
  // and a JSON string ``'false'`` is truthy in JS and would silently disable a
  // perfectly good anchor.
  if (fv.contested === true) {
    s.contested = true;
  }
  return s;
}

const recordRole = (v: unknown): RecordRole =>
  v === 'contracted' || v === 'payed' ? v : 'planned';

const recordSource = (v: unknown): 'excel' | 'user' | undefined => {
  if (v === 'user') return 'user';
  if (v === 'excel' || v === 'ws26_27_excel') return 'excel';
  return undefined;
};

/**
 * ``fact_type: 'liquiditaet_record'`` → a lifecycle record. ``total_eur`` is
 * authoritative; when absent it falls back to ``qty × unit_price_eur``. A
 * missing ``unit_price_eur`` defaults to the total (a plain qty=1 line). Stays
 * defensive so one malformed row never crashes the page.
 */
export function toRecord(fact: DerivedFact): LiquiditaetRecord {
  const fv = fact.fact_value;
  const category = ledgerCategory(fv);
  const qty = num(fv.qty, 1) || 1;
  const unitPrice = num(fv.unit_price_eur);
  const total = fv.total_eur != null ? num(fv.total_eur) : round2(qty * unitPrice);
  return {
    transaction_id: str(fv.transaction_id) || (fact.derived_fact_id ?? ''),
    record_role: recordRole(fv.record_role),
    qty,
    unit_price_eur: unitPrice || total,
    total_eur: total,
    value_date: str(fv.value_date),
    ledger_category: category,
    direction: direction(fv.direction),
    label: str(fv.label) || categoryLabel(category),
    period_ym: str(fv.period_ym) || str(fv.value_date).slice(0, 7),
    source: recordSource(fv.source),
    doc_ref: typeof fv.doc_ref === 'string' ? fv.doc_ref : undefined,
    statement_id: typeof fv.statement_id === 'string' ? fv.statement_id : undefined,
    notes: typeof fv.notes === 'string' && fv.notes ? fv.notes : undefined,
    derived_fact_id: fact.derived_fact_id,
    // #2211: carry the record's owning account when present (native records have
    // none today; conditional key so account-less rows stay account-less).
    ...(typeof fv.account_id === 'string' && fv.account_id ? { account_id: fv.account_id } : {}),
  };
}
